// Simple API utility for backend integration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const isPublicAuthEndpoint = (endpoint: string): boolean => {
    return [
        '/auth/login',
        '/auth/refresh-token',
        '/auth/forgot-password',
        '/auth/reset-password',
    ].includes(endpoint);
};
const redirectToParentLogin = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_user');
    localStorage.removeItem('token');
    if (window.location.pathname !== '/test-integration') {
        window.location.href = `${import.meta.env.VITE_HRMS_FRONTEND_URL}/login`;
    }
};

// Simple fetch wrapper
export const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;

    const defaultHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
    };

    // Get token from localStorage if it exists
    const token = localStorage.getItem('auth_token');
    if (token) {
        defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    try {
        const response = await fetch(url, {
            ...options,
            headers: {
                ...defaultHeaders,
                ...options.headers,
            },
            credentials: 'include', // Include cookies for authentication
        });

        const data = await response.json();

        if (!response.ok) {
            // Any unauthorized response on protected endpoints should force re-login.
            if (response.status === 401 && !isPublicAuthEndpoint(endpoint)) {
                console.log('Unauthorized request, redirecting to HRMS login');
                redirectToParentLogin();
                return { success: false, message: 'Unauthorized' };
            }
            throw new Error(data.message || 'API request failed');
        }

        return data;
    } catch (error) {
        console.error('API Error:', error);
        throw error;
    }
};

// Simple API methods
export const api = {
    // GET request
    get: (endpoint: string) => apiCall(endpoint, { method: 'GET' }),

    // POST request
    post: (endpoint: string, data?: any) =>
        apiCall(endpoint, {
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        }),

    // PUT request
    put: (endpoint: string, data?: any) =>
        apiCall(endpoint, {
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        }),

    // PATCH request
    patch: (endpoint: string, data?: any) =>
        apiCall(endpoint, {
            method: 'PATCH',
            body: data ? JSON.stringify(data) : undefined,
        }),

    // DELETE request
    delete: (endpoint: string) => apiCall(endpoint, { method: 'DELETE' }),
};

// Auth helpers
export const authAPI = {
    login: async (email: string, password: string) => {
        const response = await api.post('/auth/login', { email, password });
        if (response.success && response.data?.token) {
            localStorage.setItem('auth_token', response.data.token);
            localStorage.setItem('current_user', JSON.stringify(response.data.user));
        }
        return response;
    },

    logout: async () => {
        console.log('🔄 Calling backend logout API...');
        try {
            // Call backend logout with cookies
            const response = await fetch(`${API_BASE_URL}/auth/logout`, {
                method: 'POST',
                credentials: 'include', // Important: include cookies
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();
            console.log('📡 Backend logout response:', data);

            if (response.ok) {
                console.log('✅ Backend logout successful');
            } else {
                console.error('❌ Backend logout failed:', data);
            }

            return data;
        } catch (error) {
            console.error('❌ Logout API error:', error);
            throw error;
        } finally {
            // Always clear local storage regardless of API success/failure
            localStorage.removeItem('auth_token');
            localStorage.removeItem('current_user');
            localStorage.removeItem('token');
            console.log('🧹 Local storage cleared');
        }
    },

    // Check if user is authenticated via cookies (backend session)
    checkAuth: async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/secure`, {
                method: 'GET',
                credentials: 'include', // Include cookies
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // Store user data if authenticated
                if (data.user) {
                    localStorage.setItem('current_user', JSON.stringify(data.user));
                }
                // Store new access token if provided
                if (data.newAccessToken) {
                    localStorage.setItem('auth_token', data.newAccessToken);
                }
                return data;
            }

            // If auth cookie/token is missing or expired, force parent login.
            if (response.status === 401) {
                console.log('Unauthorized during auth check, redirecting to HRMS login');
                redirectToParentLogin();
                return { success: false, message: data.message || 'Unauthorized' };
            }

            return { success: false, message: 'Not authenticated' };
        } catch (error) {
            console.error('Auth check error:', error);
            return { success: false, message: 'Auth check failed' };
        }
    },

    getCurrentUser: () => {
        const userStr = localStorage.getItem('current_user');
        return userStr ? JSON.parse(userStr) : null;
    },
};

// Dashboard API
export const dashboardAPI = {
    getStats: () => api.get('/dashboard'),
    getProgress: () => api.get('/dashboard/progress'),
    getActivities: () => api.get('/dashboard/activities'),
};

// Tasks API
export const tasksAPI = {
    getTasks: () => api.get('/tasks/all'),
    getMyTasks: () => api.get('/tasks/my'),
    getBuddyAssignments: () => api.get('/tasks/assignments/buddy'),
    getMyAssignments: () => api.get('/tasks/assignments/my'),
    getAllAssignments: () => api.get('/tasks/assignments/all'),
    getTeamAssignments: () => api.get('/tasks/assignments/team'),
    createTask: (taskData: any) => api.post('/tasks', taskData),
    updateTask: (taskId: string, taskData: any) => api.put(`/tasks/${taskId}`, taskData),
    deleteTask: (taskId: string) => api.delete(`/tasks/${taskId}`),

    // Task assignment status updates (this will also create/update progress records)
    updateTaskStatus: (taskId: string, statusData: {
        status: string;
        notes?: string;
        score?: number;
        submissionUrl?: string;
        timeSpent?: number;
        progressPercentage?: number;
    }) => api.put(`/tasks/assignments/${taskId}/status`, statusData),

    // Legacy endpoint (may not exist on backend)
    completeTask: (taskId: string) => api.post(`/tasks/${taskId}/complete`),

    // Overdue tasks
    getAllOverdueTasks: () => api.get('/tasks/overdue/all'),
    getBuddyOverdueTasks: () => api.get('/tasks/overdue/buddy'),
    getMyOverdueTasks: () => api.get('/tasks/overdue/my'),
    getTeamOverdueTasks: () => api.get('/tasks/overdue/team'),

    // Task analytics
    getTaskAnalytics: () => api.get('/analytics/tasks/analytics'),
};

// Users API
export const usersAPI = {
    getUsers: () => api.get('/users'),
    getUserById: (userId: string) => api.get(`/users/${userId}`),
    updateUser: (userId: string, userData: any) => api.put(`/users/${userId}`, userData),
};

// Team API
export const teamAPI = {
    // Get all teams with members
    getAllTeams: () => api.get('/team/teams'),

    // Get specific team by ID
    getTeamById: (teamId: string) => api.get(`/team/teams/${teamId}`),

    // Get current user's team role
    getMyTeamRole: async () => {
        console.log('🔍 Calling team role API: /team/teams/teamrole');
        try {
            // Use direct fetch with explicit cookie handling like checkAuth
            const response = await fetch(`${API_BASE_URL}/team/teams/teamrole`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();
            console.log('📡 Team role raw response:', data);

            if (!response.ok) {
                throw new Error(data.message || 'Team role API request failed');
            }

            console.log('✅ Team role API success:', data);
            return data;
        } catch (error) {
            console.error('❌ Team role API failed:', error);
            throw error;
        }
    },

    // Get team role by email (HR only)
    getTeamRoleByEmail: (email: string) => api.get(`/team/teams/teamrole/${email}`),

    // Team management (HR only)
    createTeam: (teamData: any) => api.post('/team/teams', teamData),
    updateTeam: (teamId: string, teamData: any) => api.put(`/team/teams/${teamId}`, teamData),
    deleteTeam: (teamId: string) => api.delete(`/team/teams/${teamId}`),

    // Team member management (HR only)
    addTeamMember: (teamId: string, memberData: any) => api.post(`/team/teams/${teamId}/members`, memberData),
    removeTeamMember: (teamId: string, userId: string) => api.delete(`/team/teams/${teamId}/members/${userId}`),

    // Buddy assignment (HR only)
    assignBuddy: (assignmentData: {
        assignments: Array<{
            buddy_email: string;
            joiner_emails: string[];
        }>;
        team_id: string;
        notes?: string;
    }) => api.post('/team/teams/assign-buddy', assignmentData),
    // Get active joiners
    getActiveJoiners: () => api.get('/team/teams/active-joiners'),

    // Get buddy's joiners (filtered by team_id)
    getBuddyJoiners: () => api.get('/team/teams/buddy-joiners'),

    // Get employees filtered by designation for buddy/joiner assignment
    getEmployeesByDesignation: (teamId?: string) => {
        const queryParams = teamId ? `?team_id=${teamId}` : '';
        return api.get(`/team/teams/employees-by-designation${queryParams}`);
    },

    // Get team members for team lead
    getTeamLeadTeamMembers: () => api.get('/team/teams/team-lead-members'),

    // Get team lead dashboard data (overdue tasks, buddy performance, joinee progress)
    getTeamLeadDashboardData: () => api.get('/team/teams/team-lead-dashboard'),

    // Assign buddy to joiner (create new assignments)
    assignBuddy: (data: any) => api.post('/buddy/team-assign', data),

    // Update buddy assignments (add to existing assignments)
    updateBuddyAssignments: (data: any) => api.put('/buddy/team-assignments', data),

    // Get current buddy assignments for a team
    getTeamAssignments: (teamId: string) => api.get(`/buddy/team-assignments/${teamId}`),

    // Disassign all buddies from a team
    disassignTeamBuddies: (teamId: string) => api.delete(`/buddy/team-assignments/${teamId}`),

    // Disassign individual buddy (just change role to MEMBER)
    disassignIndividualBuddy: (teamId: string, buddyEmail: string) => 
      api.patch(`/buddy/team-assignments/${teamId}/buddy/${encodeURIComponent(buddyEmail)}`),

    // Disassign a specific joiner from a buddy
    disassignJoinerFromBuddy: (teamId: string, buddyEmail: string, joinerEmail: string) => 
      api.delete(`/buddy/team-assignments/${teamId}/buddy/${encodeURIComponent(buddyEmail)}/joiner/${encodeURIComponent(joinerEmail)}`),

    // Check if team has eligible members for buddy assignment
    checkTeamEligibility: (teamId: string) => api.get(`/buddy/team-eligibility/${teamId}`),

    // HRMS-style team management endpoints
    getCreateTeamData: () => api.get('/team/hrms/createTeam'),
    getEditTeamData: (teamId: string) => api.get(`/team/hrms/editTeam/${teamId}`),
    getTeamsHRMS: (status?: string) => {
        const queryParams = status ? `?status=${status}` : '';
        return api.get(`/team/hrms/teams${queryParams}`);
    },
    createTeamHRMS: (teamData: {
        team_name: string;
        team_lead_email: string;
        team_member_emails: string[];
    }) => api.post('/team/hrms/teams', teamData),
    updateTeamHRMS: (teamId: string, teamData: {
        team_name: string;
        team_lead_email: string;
        team_member_emails: string[];
    }) => api.put(`/team/hrms/teams/${teamId}`, teamData),
    deleteTeamHRMS: (teamId: string) => api.delete(`/team/hrms/teams/${teamId}`),

};

// Reviews API
export const reviewsAPI = {
    // Get all reviews for buddy
    getMyReviews: () => api.get('/review/my'),

    // Get reviews for a specific joiner (buddy/manager access)
    getJoineeReviews: (joineeEmail: string) => api.get(`/review/joinee/${joineeEmail}/reviews`),

    // Get my reviews as a joiner (joiner access)
    getJoineeMyReviews: () => api.get('/review/joinee/my-reviews'),

    // Get lab reviews
    getLabReviews: () => api.get('/review/lab'),

    // Create task review
    createReview: (reviewData: any) => api.post('/review', reviewData),

    // Create lab review
    createLabReview: (reviewData: any) => api.post('/review/lab', reviewData),

    // Get specific review
    getReview: (reviewId: string) => api.get(`/review/${reviewId}`),

    // Update review
    updateReview: (reviewId: string, reviewData: any) => api.put(`/review/${reviewId}`, reviewData),

    // Delete review
    deleteReview: (reviewId: string) => api.delete(`/review/${reviewId}`),

    // Get review statistics
    getReviewStats: () => api.get('/review/stats'),

    // Admin review functions
    getBuddyReviews: () => api.get('/review/buddy'),
    submitHRReview: (reviewData: any) => api.post('/review/hr', reviewData),
    submitBuddyReview: (reviewData: any) => api.post('/review/buddy', reviewData),
    submitReview: (reviewData: any) => api.post('/review', reviewData),
};

// Progress API
export const progressAPI = {
    // Get my progress
    getMyProgress: () => api.get('/progress/my'),

    // Get my progress statistics
    getMyProgressStats: () => api.get('/progress/my/stats'),

    // Update my task progress
    updateMyProgress: (taskId: string, progressData: any) => api.put(`/progress/my/task/${taskId}`, progressData),

    // Get progress for specific user (buddy/HR access)
    getUserProgress: (userId: string) => api.get(`/progress/user/${userId}`),

    // Get progress statistics for specific user
    getUserProgressStats: (userId: string) => api.get(`/progress/user/${userId}/stats`),

    // Get progress for specific task
    getTaskProgress: (taskId: string) => api.get(`/progress/task/${taskId}`),

    // Get overall progress statistics (HR/Admin)
    getOverallStats: () => api.get('/progress/stats/overall'),

    // Update progress for user and task (HR/Admin/Buddy)
    updateProgress: (userId: string, taskId: string, progressData: any) => api.put(`/progress/${userId}/${taskId}`, progressData),

    // Get progress by ID
    getProgress: (progressId: string) => api.get(`/progress/${progressId}`),
};

// Buddy API
export const buddyAPI = {
    // Get buddy's assignments
    getMyAssignments: () => api.get('/buddy/my-assignments'),

    // Get buddy's joiners (simplified for task creation)
    getMyJoiners: () => api.get('/buddy/my-joiners'),

    // Get all assignments (HR/Admin)
    getAssignments: (params?: { buddyId?: string; joinerId?: string }) => {
        const queryParams = new URLSearchParams();
        if (params?.buddyId) queryParams.append('buddyId', params.buddyId);
        if (params?.joinerId) queryParams.append('joinerId', params.joinerId);
        const queryString = queryParams.toString();
        return api.get(`/buddy/assignments${queryString ? `?${queryString}` : ''}`);
    },

    // Get available buddies
    getAvailableBuddies: () => api.get('/buddy/available'),

    // Assign buddy to joiner
    assignBuddy: (assignmentData: any) => api.post('/buddy/assign', assignmentData),

    // Auto-assign buddy
    autoAssignBuddy: (joinerId: string) => api.post(`/buddy/auto-assign/${joinerId}`),

    // Deactivate assignment
    deactivateAssignment: (assignmentId: string) => api.put(`/buddy/assignments/${assignmentId}/deactivate`),

    // Get assignment statistics
    getAssignmentStats: () => api.get('/buddy/stats'),
};
// Notifications API
export const notificationsAPI = {
    // Create a single notification (HR/Admin only)
    createNotification: (notificationData: any) => api.post('/notifications', notificationData),

    // Create bulk notifications (HR/Admin only)
    //createBulkNotification: (bulkData: any) => api.post('/notifications/bulk', bulkData),

    // Get current user's notifications
    getMyNotifications: () => api.get('/notifications/my'),

    // Get unread count for current user
    getUnreadCount: () => api.get('/notifications/my/unread-count'),

    // Mark all current user's notifications as read
    markAllAsRead: () => api.put('/notifications/my/mark-all-read'),

    // Get notification statistics (HR/Admin only)
    getNotificationStats: () => api.get('/notifications/stats'),

    // Get notifications for a specific user (HR/Admin only)
    getUserNotifications: (userEmail: string) => api.get(`/notifications/user/${userEmail}`),


    // Get unread count for a specific user (HR/Admin only)
    //getUserUnreadCount: (userId: string) => api.get(`/notifications/user/${userId}/unread-count`),

    // Get a specific notification by ID
    getNotificationById: (id: string) => api.get(`/notifications/${id}`),

    // Mark a specific notification as read
    markAsRead: (id: string) => api.put(`/notifications/${id}/read`),

    // Delete a specific notification
    deleteNotification: (id: string) => api.delete(`/notifications/${id}`),
};

// Achievements API
export const achievementsAPI = {
    // Get my achievements
    getMyAchievements: () => api.get('/achievements/my-achievements'),
    
    // Get leaderboard
    getLeaderboard: () => api.get('/achievements/leaderboard'),
    
    // Get all achievements (HR/Admin only)
    getAllAchievements: () => api.get('/achievements/all'),
};

// Admin API - combines HR and Manager functionalities
export const adminAPI = {
    // Dashboard and overview
    getDashboard: () => api.get('/admin/dashboard'),
    getSystemHealth: () => api.get('/admin/system-health'),
    validateAccess: () => api.get('/admin/validate-access'),
    
    // User management
    getAllUserRoles: () => api.get('/admin/user-roles'),
    updateUserRole: (userId: string, roleData: { role?: string; teamRole?: string }) => 
        api.put(`/admin/user-roles/${userId}`, roleData),
    
    // Analytics
    getComprehensiveAnalytics: () => api.get('/admin/analytics'),
    
    // HR Functions (accessible to admin)
    hr: {
        getAllEmployees: () => api.get('/users/all'),
        createEmployee: (userData: any) => api.post('/users', userData),
        updateEmployee: (id: string, userData: any) => api.put(`/users/${id}`, userData),
        deleteEmployee: (id: string) => api.delete(`/users/${id}`),
        getEmployeesByRole: (role: string) => api.get(`/users/role/${role}`),
        
        // Team management
        createTeam: (teamData: any) => api.post('/team/teams', teamData),
        updateTeam: (id: string, teamData: any) => api.put(`/team/teams/${id}`, teamData),
        deleteTeam: (id: string) => api.delete(`/team/teams/${id}`),
        addTeamMember: (teamId: string, memberData: any) => 
            api.post(`/team/teams/${teamId}/members`, memberData),
        removeTeamMember: (teamId: string, userId: string) => 
            api.delete(`/team/teams/${teamId}/members/${userId}`),
        
        // Analytics
        getHRAnalytics: () => api.get('/analytics/stats/overall'),
        getDepartmentAnalytics: () => api.get('/analytics/departments/analytics'),
    },
    
    // Team Lead Functions (accessible to admin)
    teamLead: {
        getMyTeam: () => api.get('/team/teams/team-lead-members'),
        getTeamLeadDashboard: () => api.get('/team/teams/team-lead-dashboard'),
        assignTask: (taskData: any) => api.post('/tasks', taskData),
        getOverdueTasks: () => api.get('/tasks/overdue/all'),
        
        // Team performance
        getTeamPerformance: () => api.get('/analytics/tasks/analytics'),
        getBuddyPerformance: (buddyId: string) => 
            api.get(`/analytics/buddy/${buddyId}/performance`),
        
        // Reviews
        getManagerReviews: () => api.get('/review/manager'),
        submitReview: (reviewData: any) => api.post('/review', reviewData),
    }
};

export default api;


